import React from 'react';
import { Check, X, TrendingDown, TrendingUp, Trash2 } from 'lucide-react';
import type { Invoice, Tariff, SimulationResult, ComparisonColumn } from '../lib/types';
import { formatEuros, formatDifference, formatPercentage, getInvoiceTotal, getSimulationTotal } from '../lib/simulate';

interface CompareGridProps {
  columns: ComparisonColumn[];
  onDeleteColumn?: (index: number) => void;
  showWithVAT?: boolean;
}

export function CompareGrid({ columns, onDeleteColumn, showWithVAT = true }: CompareGridProps) {
  if (columns.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-apple-gray-200 p-8 text-center">
        <div className="text-apple-gray-400 mb-4">
          <div className="w-16 h-16 mx-auto bg-apple-gray-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
              <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-apple-gray-600 mb-2">
            Comparador de Tarifas Eléctricas
          </h3>
          <p className="text-apple-gray-400">
            Sube tu factura y añade tarifas para comenzar la comparación
          </p>
        </div>
      </div>
    );
  }

  const invoiceColumn = columns.find(col => col.type === 'invoice');
  const invoice = invoiceColumn?.data as Invoice;

  return (
    <div className="bg-white rounded-xl border border-apple-gray-200 overflow-hidden">
      {/* Header */}
      <div className="bg-apple-gray-50 border-b border-apple-gray-200 p-4">
        <h2 className="text-xl font-semibold text-apple-gray-800">
          Comparación de Costes
        </h2>
        <p className="text-sm text-apple-gray-500 mt-1">
          Analiza los costes de tu factura vs diferentes tarifas comerciales
        </p>
      </div>

      {/* Comparison Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          {/* Column Headers */}
          <thead className="bg-apple-gray-50">
            <tr>
              <th className="text-left p-4 font-medium text-apple-gray-600 border-b border-apple-gray-200 min-w-[150px]">
                Concepto
              </th>
              {columns.map((column, index) => (
                <th key={index} className="text-center p-4 font-medium text-apple-gray-800 border-b border-apple-gray-200 min-w-[120px]">
                  <div className="flex flex-col items-center">
                    <div className="flex items-center gap-2 mb-1">
                      {column.type === 'invoice' ? (
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      ) : (
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      )}
                      <span className="font-semibold">
                        {column.type === 'invoice' ? 'Factura Original' : (column.data as Tariff).name}
                      </span>
                      {/* Botón de eliminar solo para tarifas (no para facturas) */}
                      {column.type !== 'invoice' && onDeleteColumn && (
                        <button
                          onClick={() => onDeleteColumn(index)}
                          className="ml-1 p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full transition-colors"
                          title="Eliminar esta tarifa"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    {column.simulation?.vs_invoice && (
                      <div className={`text-xs font-medium px-2 py-1 rounded-full ${
                        column.simulation.vs_invoice.is_cheaper 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {column.simulation.vs_invoice.is_cheaper ? (
                          <div className="flex items-center gap-1">
                            <TrendingDown className="w-3 h-3" />
                            <span>Más barata</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1">
                            <TrendingUp className="w-3 h-3" />
                            <span>Más cara</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {/* Potencia contratada de la factura original */}
            {invoiceColumn && (
              <>
                <tr className="bg-purple-50 border-b border-purple-200">
                  <td className="p-4 font-medium text-purple-800" colSpan={columns.length + 1}>
                    ⚡ Potencia Contratada (Factura Original)
                  </td>
                </tr>
                <tr className="border-b border-apple-gray-100 hover:bg-apple-gray-50">
                  <td className="p-4 font-medium text-apple-gray-700">
                    ⚡ Potencia P1 (Punta)
                  </td>
                  <td className="p-4 text-center text-apple-gray-800">
                    {formatPower((invoice as Invoice).power.p1_kw)}
                  </td>
                  {columns.slice(1).map((_, index) => (
                    <td key={index} className="p-4 text-center text-apple-gray-400">—</td>
                  ))}
                </tr>
                <tr className="border-b border-apple-gray-100 hover:bg-apple-gray-50">
                  <td className="p-4 font-medium text-apple-gray-700">
                    ⚡ Potencia P2 (Valle)
                  </td>
                  <td className="p-4 text-center text-apple-gray-800">
                    {formatPower((invoice as Invoice).power.p2_kw)}
                  </td>
                  {columns.slice(1).map((_, index) => (
                    <td key={index} className="p-4 text-center text-apple-gray-400">—</td>
                  ))}
                </tr>
              </>
            )}

            {/* Consumos de la factura original */}
            {invoiceColumn && (
              <>
                <tr className="bg-blue-50 border-b border-blue-200">
                  <td className="p-4 font-medium text-blue-800" colSpan={columns.length + 1}>
                    📊 Consumos de la Factura Original
                  </td>
                </tr>
                <tr className="border-b border-apple-gray-100 hover:bg-apple-gray-50">
                  <td className="p-4 font-medium text-apple-gray-700">
                    📈 Consumo P1 (Punta)
                  </td>
                  <td className="p-4 text-center text-apple-gray-800">
                    {formatConsumption((invoice as Invoice).energy.p1_kwh)}
                  </td>
                  {columns.slice(1).map((_, index) => (
                    <td key={index} className="p-4 text-center text-apple-gray-400">—</td>
                  ))}
                </tr>
                <tr className="border-b border-apple-gray-100 hover:bg-apple-gray-50">
                  <td className="p-4 font-medium text-apple-gray-700">
                    📈 Consumo P2 (Llano)
                  </td>
                  <td className="p-4 text-center text-apple-gray-800">
                    {formatConsumption((invoice as Invoice).energy.p2_kwh)}
                  </td>
                  {columns.slice(1).map((_, index) => (
                    <td key={index} className="p-4 text-center text-apple-gray-400">—</td>
                  ))}
                </tr>
                <tr className="border-b border-apple-gray-100 hover:bg-apple-gray-50">
                  <td className="p-4 font-medium text-apple-gray-700">
                    📈 Consumo P3 (Valle)
                  </td>
                  <td className="p-4 text-center text-apple-gray-800">
                    {formatConsumption((invoice as Invoice).energy.p3_kwh)}
                  </td>
                  {columns.slice(1).map((_, index) => (
                    <td key={index} className="p-4 text-center text-apple-gray-400">—</td>
                  ))}
                </tr>
                <tr className="border-b border-apple-gray-100 hover:bg-apple-gray-50">
                  <td className="p-4 font-bold text-apple-gray-700">
                    📈 Consumo Total
                  </td>
                  <td className="p-4 text-center font-bold text-apple-gray-800">
                    {formatConsumption(
                      ((invoice as Invoice).energy.p1_kwh || 0) + 
                      ((invoice as Invoice).energy.p2_kwh || 0) + 
                      ((invoice as Invoice).energy.p3_kwh || 0)
                    )}
                  </td>
                  {columns.slice(1).map((_, index) => (
                    <td key={index} className="p-4 text-center text-apple-gray-400">—</td>
                  ))}
                </tr>
                
                <tr className="bg-amber-50 border-b border-amber-200">
                  <td className="p-4 font-medium text-amber-800" colSpan={columns.length + 1}>
                    💰 Precios Unitarios
                  </td>
                </tr>
              </>
            )}
            
            {/* Precio Potencia P1 */}
            <tr className="border-b border-apple-gray-100 hover:bg-apple-gray-50">
              <td className="p-4 font-medium text-apple-gray-700">
                💰 Precio Potencia P1
              </td>
              {columns.map((column, index) => (
                <td key={index} className="p-4 text-center text-apple-gray-800">
                  {column.type === 'invoice' 
                    ? formatPowerRate((column.data as Invoice).power.p1_eur_kw_day, true, showWithVAT)
                    : formatPowerRateForTariff((column.data as Tariff), 'p1', showWithVAT)
                  }
                </td>
              ))}
            </tr>

            {/* Precio Potencia P2 */}
            <tr className="border-b border-apple-gray-100 hover:bg-apple-gray-50">
              <td className="p-4 font-medium text-apple-gray-700">
                💰 Precio Potencia P2
              </td>
              {columns.map((column, index) => (
                <td key={index} className="p-4 text-center text-apple-gray-800">
                  {column.type === 'invoice' 
                    ? formatPowerRate((column.data as Invoice).power.p2_eur_kw_day, true, showWithVAT)
                    : formatPowerRateForTariff((column.data as Tariff), 'p2', showWithVAT)
                  }
                </td>
              ))}
            </tr>

            {/* Precio Potencia P3 */}
            <tr className="border-b border-apple-gray-100 hover:bg-apple-gray-50">
              <td className="p-4 font-medium text-apple-gray-700">
                💰 Precio Potencia P3
              </td>
              {columns.map((column, index) => (
                <td key={index} className="p-4 text-center text-apple-gray-800">
                  {column.type === 'invoice' 
                    ? formatPowerRate((column.data as Invoice).power.p3_eur_kw_day, true, showWithVAT)
                    : formatPowerRateForTariff((column.data as Tariff), 'p3', showWithVAT)
                  }
                </td>
              ))}
            </tr>

            {/* Coste Potencia P1 */}
            <tr className="border-b border-apple-gray-100 hover:bg-apple-gray-50">
              <td className="p-4 font-medium text-apple-gray-700">
                Coste Potencia P1 (Punta)
              </td>
              {columns.map((column, index) => (
                <td key={index} className="p-4 text-center text-apple-gray-800">
                  {column.type === 'invoice' 
                    ? getInvoicePowerP1(column.data as Invoice)
                    : formatEuros(column.simulation?.breakdown.power_p1 || null)
                  }
                </td>
              ))}
            </tr>

            {/* Coste Potencia P2 */}
            <tr className="border-b border-apple-gray-100 hover:bg-apple-gray-50">
              <td className="p-4 font-medium text-apple-gray-700">
                Coste Potencia P2 (Llano)
              </td>
              {columns.map((column, index) => (
                <td key={index} className="p-4 text-center text-apple-gray-800">
                  {column.type === 'invoice' 
                    ? getInvoicePowerP2(column.data as Invoice)
                    : formatEuros(column.simulation?.breakdown.power_p2 || null)
                  }
                </td>
              ))}
            </tr>

            {/* Coste Potencia P3 */}
            <tr className="border-b border-apple-gray-100 hover:bg-apple-gray-50">
              <td className="p-4 font-medium text-apple-gray-700">
                Coste Potencia P3 (Valle)
              </td>
              {columns.map((column, index) => (
                <td key={index} className="p-4 text-center text-apple-gray-800">
                  {column.type === 'invoice' 
                    ? getInvoicePowerP3(column.data as Invoice)
                    : formatEuros(column.simulation?.breakdown.power_p3 || null)
                  }
                </td>
              ))}
            </tr>

            {/* Precio Energía P1 */}
            <tr className="border-b border-apple-gray-100 hover:bg-apple-gray-50">
              <td className="p-4 font-medium text-apple-gray-700">
                💡 Precio Energía P1
              </td>
              {columns.map((column, index) => (
                <td key={index} className="p-4 text-center text-apple-gray-800">
                  {column.type === 'invoice' 
                    ? formatEnergyRate((column.data as Invoice).energy.p1_eur_kwh, true, showWithVAT)
                    : formatEnergyRateForTariff((column.data as Tariff), 'p1', showWithVAT)
                  }
                </td>
              ))}
            </tr>

            {/* Precio Energía P2 */}
            <tr className="border-b border-apple-gray-100 hover:bg-apple-gray-50">
              <td className="p-4 font-medium text-apple-gray-700">
                💡 Precio Energía P2
              </td>
              {columns.map((column, index) => (
                <td key={index} className="p-4 text-center text-apple-gray-800">
                  {column.type === 'invoice' 
                    ? formatEnergyRate((column.data as Invoice).energy.p2_eur_kwh, true, showWithVAT)
                    : formatEnergyRateForTariff((column.data as Tariff), 'p2', showWithVAT)
                  }
                </td>
              ))}
            </tr>

            {/* Precio Energía P3 */}
            <tr className="border-b border-apple-gray-100 hover:bg-apple-gray-50">
              <td className="p-4 font-medium text-apple-gray-700">
                💡 Precio Energía P3
              </td>
              {columns.map((column, index) => (
                <td key={index} className="p-4 text-center text-apple-gray-800">
                  {column.type === 'invoice' 
                    ? formatEnergyRate((column.data as Invoice).energy.p3_eur_kwh, true, showWithVAT)
                    : formatEnergyRateForTariff((column.data as Tariff), 'p3', showWithVAT)
                  }
                </td>
              ))}
            </tr>

            {/* Separador para costes calculados */}
            <tr className="bg-green-50 border-b border-green-200">
              <td className="p-4 font-medium text-green-800" colSpan={columns.length + 1}>
                🧮 Costes Calculados
              </td>
            </tr>
            
            {/* Coste Energía P1 */}
            <tr className="border-b border-apple-gray-100 hover:bg-apple-gray-50">
              <td className="p-4 font-medium text-apple-gray-700">
                Coste Energía P1 (Punta)
              </td>
              {columns.map((column, index) => (
                <td key={index} className="p-4 text-center text-apple-gray-800">
                  {column.type === 'invoice' 
                    ? getInvoiceEnergyP1(column.data as Invoice)
                    : formatEuros(column.simulation?.breakdown.energy_p1 || null)
                  }
                </td>
              ))}
            </tr>

            {/* Coste Energía P2 */}
            <tr className="border-b border-apple-gray-100 hover:bg-apple-gray-50">
              <td className="p-4 font-medium text-apple-gray-700">
                Coste Energía P2 (Llano)
              </td>
              {columns.map((column, index) => (
                <td key={index} className="p-4 text-center text-apple-gray-800">
                  {column.type === 'invoice' 
                    ? getInvoiceEnergyP2(column.data as Invoice)
                    : formatEuros(column.simulation?.breakdown.energy_p2 || null)
                  }
                </td>
              ))}
            </tr>

            {/* Coste Energía P3 */}
            <tr className="border-b border-apple-gray-100 hover:bg-apple-gray-50">
              <td className="p-4 font-medium text-apple-gray-700">
                Coste Energía P3 (Valle)
              </td>
              {columns.map((column, index) => (
                <td key={index} className="p-4 text-center text-apple-gray-800">
                  {column.type === 'invoice' 
                    ? getInvoiceEnergyP3(column.data as Invoice)
                    : formatEuros(column.simulation?.breakdown.energy_p3 || null)
                  }
                </td>
              ))}
            </tr>

            {/* Separador para extras e impuestos */}
            <tr className="bg-purple-50 border-b border-purple-200">
              <td className="p-4 font-medium text-purple-800" colSpan={columns.length + 1}>
                📋 Extras e Impuestos
              </td>
            </tr>

            {/* Otros Cargos */}
            <tr className="border-b border-apple-gray-100 hover:bg-apple-gray-50">
              <td className="p-4 font-medium text-apple-gray-700">
                Otros Cargos
              </td>
              {columns.map((column, index) => (
                <td key={index} className="p-4 text-center text-apple-gray-800">
                  {column.type === 'invoice' 
                    ? formatEuros((column.data as Invoice).extras.equipment)
                    : formatEuros(column.simulation?.breakdown.extras_equipment || null)
                  }
                </td>
              ))}
            </tr>

            {/* Descuentos */}
            <tr className="border-b border-apple-gray-100 hover:bg-apple-gray-50">
              <td className="p-4 font-medium text-apple-gray-700">
                Descuentos/Bonos
              </td>
              {columns.map((column, index) => (
                <td key={index} className="p-4 text-center text-apple-gray-800">
                  {column.type === 'invoice' 
                    ? formatEuros((column.data as Invoice).extras.social_bonus)
                    : formatEuros(column.simulation?.breakdown.extras_social_bonus || null)
                  }
                </td>
              ))}
            </tr>

            {/* Impuesto Eléctrico */}
            <tr className="border-b border-apple-gray-100 hover:bg-apple-gray-50">
              <td className="p-4 font-medium text-apple-gray-700">
                Impuesto Eléctrico
              </td>
              {columns.map((column, index) => (
                <td key={index} className="p-4 text-center text-apple-gray-800">
                  {column.type === 'invoice' 
                    ? formatEuros((column.data as Invoice).taxes.electricity_tax)
                    : formatEuros(column.simulation?.breakdown.electricity_tax || null)
                  }
                </td>
              ))}
            </tr>

            {/* IVA - Solo mostrar si showWithVAT está activo */}
            {showWithVAT && (
              <tr className="border-b border-apple-gray-100 hover:bg-apple-gray-50">
                <td className="p-4 font-medium text-apple-gray-700">
                  IVA (21%)
                </td>
                {columns.map((column, index) => (
                  <td key={index} className="p-4 text-center text-apple-gray-800">
                    {column.type === 'invoice' 
                      ? formatEuros((column.data as Invoice).taxes.vat)
                      : formatEuros(column.simulation?.breakdown.vat || null)
                    }
                  </td>
                ))}
              </tr>
            )}

            {/* Total */}
            <tr className="border-b-2 border-apple-gray-300 bg-apple-gray-50">
              <td className="p-4 font-bold text-apple-gray-800 text-lg">
                TOTAL {showWithVAT ? '(con IVA)' : '(sin IVA)'}
              </td>
              {columns.map((column, index) => (
                <td key={index} className="p-4 text-center">
                  <div className="font-bold text-lg text-apple-gray-800">
                    {column.type === 'invoice' 
                      ? formatEuros(getInvoiceTotal(column.data as Invoice, showWithVAT))
                      : column.simulation 
                        ? formatEuros(getSimulationTotal(column.simulation, showWithVAT))
                        : formatEuros(null)
                    }
                  </div>
                </td>
              ))}
            </tr>

            {/* Diferencia vs Factura */}
            <tr className="bg-white">
              <td className="p-4 font-bold text-apple-gray-800">
                Diferencia vs Factura
              </td>
              {columns.map((column, index) => (
                <td key={index} className="p-4 text-center">
                  {column.type === 'invoice' ? (
                    <span className="text-apple-gray-400 font-medium">—</span>
                  ) : column.simulation ? (
                    (() => {
                      const invoiceColumn = columns.find(col => col.type === 'invoice');
                      if (!invoiceColumn) return <span className="text-apple-gray-400">—</span>;
                      
                      const invoiceTotal = getInvoiceTotal(invoiceColumn.data as Invoice, showWithVAT);
                      const simulationTotal = getSimulationTotal(column.simulation, showWithVAT);
                      const difference = simulationTotal - invoiceTotal;
                      const percentage = invoiceTotal > 0 ? (difference / invoiceTotal) * 100 : 0;
                      
                      return (
                        <div className="space-y-1">
                          <div className={`font-bold text-lg ${formatDifference(difference).color}`}>
                            {formatDifference(difference).text}
                          </div>
                          <div className={`text-sm ${formatDifference(difference).color}`}>
                            {formatPercentage(percentage)}
                          </div>
                        </div>
                      );
                    })()
                  ) : (
                    <span className="text-apple-gray-400">—</span>
                  )}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Helper functions para extraer datos de la factura
function getInvoicePowerP1(invoice: Invoice): string {
  const kw = invoice.power.p1_kw;
  const rate = invoice.power.p1_eur_kw_day;
  const days = invoice.period.days;
  
  if (kw && rate && days) {
    const cost = kw * rate * days;
    return formatEuros(cost);
  }
  return '—';
}

function getInvoicePowerP2(invoice: Invoice): string {
  const kw = invoice.power.p2_kw;
  const rate = invoice.power.p2_eur_kw_day;
  const days = invoice.period.days;
  
  if (kw && rate && days) {
    const cost = kw * rate * days;
    return formatEuros(cost);
  }
  return '—';
}

function getInvoicePowerP3(invoice: Invoice): string {
  const kw = invoice.power.p3_kw;
  const rate = invoice.power.p3_eur_kw_day;
  const days = invoice.period.days;
  
  if (kw && rate && days) {
    const cost = kw * rate * days;
    return formatEuros(cost);
  }
  return '—';
}

function getInvoiceEnergyP1(invoice: Invoice): string {
  const kwh = invoice.energy.p1_kwh;
  const rate = invoice.energy.p1_eur_kwh;
  
  if (kwh && rate) {
    const cost = kwh * rate;
    return formatEuros(cost);
  }
  return '—';
}

function getInvoiceEnergyP2(invoice: Invoice): string {
  const kwh = invoice.energy.p2_kwh;
  const rate = invoice.energy.p2_eur_kwh;
  
  if (kwh && rate) {
    const cost = kwh * rate;
    return formatEuros(cost);
  }
  return '—';
}

function getInvoiceEnergyP3(invoice: Invoice): string {
  const kwh = invoice.energy.p3_kwh;
  const rate = invoice.energy.p3_eur_kwh;
  
  if (kwh && rate) {
    const cost = kwh * rate;
    return formatEuros(cost);
  }
  return '—';
}

// Helper function para convertir precios según estado de IVA
function adjustPriceForVATDisplay(
  originalPrice: number, 
  originalIncludesVAT: boolean, 
  displayWithVAT: boolean
): number {
  // Si no hay cambio en el estado de IVA, devolver precio original
  if (originalIncludesVAT === displayWithVAT) {
    return originalPrice;
  }
  
  // Si original incluía IVA y queremos mostrar sin IVA
  if (originalIncludesVAT && !displayWithVAT) {
    return originalPrice / 1.21;
  }
  
  // Si original no incluía IVA y queremos mostrar con IVA
  if (!originalIncludesVAT && displayWithVAT) {
    return originalPrice * 1.21;
  }
  
  return originalPrice;
}

// Helper functions para formatear precios unitarios

// Helper para formatear precios de potencia de tarifas usando regla "precio más caro cuando falta"
function formatPowerRateForTariff(tariff: Tariff, period: 'p1' | 'p2' | 'p3', displayWithVAT: boolean = true): string {
  let rate: number | null;
  let suffix = '';
  
  // Obtener precio del período solicitado
  rate = tariff.power[`${period}_eur_kw_day`];
  
  // Si el precio no existe, usar el más caro disponible
  if (rate === null || rate === undefined) {
    const availablePrices = [
      tariff.power.p1_eur_kw_day,
      tariff.power.p2_eur_kw_day,
      tariff.power.p3_eur_kw_day
    ].filter(price => price !== null && price !== undefined) as number[];
    
    if (availablePrices.length > 0) {
      rate = Math.max(...availablePrices);
      suffix = ' (precio más caro)';
    } else {
      return 'No detectado';
    }
  }
  
  if (rate === null || rate === undefined) return 'No detectado';
  
  // CRÍTICO: Usar el estado real de IVA de la tarifa
  const adjustedRate = adjustPriceForVATDisplay(rate, tariff.prices_include_vat, displayWithVAT);
  
  return `${adjustedRate.toFixed(6)} €/kW·día${suffix}`;
}

function formatPowerRate(rate: number | null, originalIncludesVAT: boolean = true, displayWithVAT: boolean = true): string {
  if (rate === null || rate === undefined) return '—';
  const adjustedRate = adjustPriceForVATDisplay(rate, originalIncludesVAT, displayWithVAT);
  return `${adjustedRate.toFixed(6)} €/kW·día`;
}

function formatEnergyRate(rate: number | null, originalIncludesVAT: boolean = true, displayWithVAT: boolean = true): string {
  if (rate === null || rate === undefined) return '—';
  const adjustedRate = adjustPriceForVATDisplay(rate, originalIncludesVAT, displayWithVAT);
  return `${adjustedRate.toFixed(6)} €/kWh`;
}

// Helper para formatear precios de energía de tarifas usando regla "precio más caro cuando falta"
function formatEnergyRateForTariff(tariff: Tariff, period: 'p1' | 'p2' | 'p3', displayWithVAT: boolean = true): string {
  const isFlatRate = !tariff.energy.p2_eur_kwh && !tariff.energy.p3_eur_kwh;
  
  let rate: number | null;
  let suffix = '';
  
  if (isFlatRate) {
    // Tarifa fija: usar siempre el precio P1
    rate = tariff.energy.p1_eur_kwh;
    suffix = ' (fija)';
  } else {
    // Obtener precio del período solicitado
    rate = tariff.energy[`${period}_eur_kwh`];
    
    // Si el precio no existe, usar el más caro disponible
    if (rate === null || rate === undefined) {
      const availablePrices = [
        tariff.energy.p1_eur_kwh,
        tariff.energy.p2_eur_kwh,
        tariff.energy.p3_eur_kwh
      ].filter(price => price !== null && price !== undefined) as number[];
      
      if (availablePrices.length > 0) {
        rate = Math.max(...availablePrices);
        suffix = ' (precio más caro)';
      } else {
        return 'No detectado';
      }
    }
  }
  
  if (rate === null || rate === undefined) return 'No detectado';
  
  // CRÍTICO: Usar el estado real de IVA de la tarifa
  const adjustedRate = adjustPriceForVATDisplay(rate, tariff.prices_include_vat, displayWithVAT);
  
  return `${adjustedRate.toFixed(6)} €/kWh${suffix}`;
}

// Helper para formatear consumo en kWh
function formatConsumption(kwh: number | null): string {
  if (kwh === null || kwh === undefined) return '—';
  return `${kwh.toFixed(0)} kWh`;
}

// Helper para formatear potencia contratada en kW
function formatPower(kw: number | null): string {
  if (kw === null || kw === undefined) return '—';
  return `${kw.toFixed(1)} kW`;
}
